import React, { useEffect, useState } from 'react';
import { Select, Input, Checkbox, Button, Spin } from 'antd';
import './SurveyThemeOption.scss';
import './surveyBaseStyles.scss';
import Card from '@Utils/Card';
import { ThemeCSSEditor } from './themeCSSEditor';
import { buildCSSTemplate } from './layouts/cssTemplate';
import { fetchThemeList } from '@Redux/slices/channel/surveyThemeSlice';
import { useDispatch, useSelector } from 'react-redux';
import {
  surveyThemeCreateFetch,
  surveyThemeView,
} from '@Redux/slices/channel/surveyThemeSlice';
import { fontsListFetch } from '@Modules/setting/productSetting/ProductSettingActions';
import { LoadingOutlined } from '@ant-design/icons';

const { Option } = Select;

const fontSizes = Array.from({ length: 10 }, (_, i) => (i + 6) * 2);
const templates = [
  { name: 'ON_SITE_CLASSIC', title: 'On-Site Classic' },
  { name: 'ON_SITE_MODAL', title: 'On-Site Modal' },
  { name: 'MOBILE_MODAL', title: 'Mobile Modal' },
];

function SurveyThemeSetting() {
  const dispatch = useDispatch();
  const themeList =
    useSelector((state) => state.channel.surveyTheme.list) || [];
  const themeView = useSelector((state) => state.channel.surveyTheme.theme);
  const loading = useSelector((state) => state.channel.surveyTheme.loading);

  const [theme, setTheme] = useState({});
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [css, setCSS] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [showThemeAttributes, setShowThemeAttributes] = useState(false);

  useEffect(() => {
    dispatch(fontsListFetch());
  }, []);

  useEffect(() => {
    setCSS(buildCSSTemplate(theme?.layout));
    if (theme?.layout) dispatch(fetchThemeList(theme?.layout));
  }, [theme?.layout]);

  useEffect(() => {
    if (theme?.layoutTheme) dispatch(surveyThemeView(theme?.layoutTheme));
  }, [theme?.layoutTheme]);

  useEffect(() => {
    if (themeView) {
      setTheme({
        ...themeView,
        layoutTheme: theme.layoutTheme,
      });
      setCSS(themeView.advancedCss || buildCSSTemplate(themeView.layout));
    }
  }, [themeView]);

  const handleThemeChange = (key, value) => {
    setTheme((prev) => ({ ...prev, [key]: value }));
  };
  const loadingSpin = <LoadingOutlined style={{ fontSize: 35 }} spin />;

  return (
    <Card>
      <div className="question-header">
        <span className="section-title">Survey Theme</span>
      </div>
      <div
        className="question-content question-with-content"
        style={{
          flexDirection: 'column',
          alignItems: 'flex-start',
          flex: 1,
          padding: 24,
        }}
      >
        <div className="layout-options">
          <div className="option">
            <label className="option-label">Layout Name</label>
            <Select
              placeholder="Choose a template"
              onChange={(value) => {
                handleThemeChange('layout', value);
                setTheme({
                  layout: value,
                });
                setEditMode(false);
                setShowThemeAttributes(false);
              }}
              style={{ width: 249 }}
            >
              {templates.map((template) => (
                <Option
                  key={template.name}
                  value={template.name}
                  disabled={
                    template.name === 'ON_SITE_MODAL' ||
                    template.name === 'MOBILE_MODAL'
                  }
                >
                  {template.title}
                </Option>
              ))}
            </Select>
          </div>
          <div className="option">
            <label className="option-label">Choose Theme</label>
            <Select
              value={theme.layoutTheme}
              style={{ width: 249 }}
              onChange={(value) => {
                handleThemeChange('layoutTheme', value);
                setEditMode(true);
                setShowThemeAttributes(true);
              }}
              placeholder="Select or create a theme"
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <div
                    style={{
                      padding: '8px',
                      cursor: 'pointer',
                      color: 'blue',
                      textAlign: 'center',
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setTheme({
                        layout: theme.layout,
                      });
                      setEditMode(false);
                      setIsDropdownVisible(false);
                      setShowThemeAttributes(true);
                    }}
                  >
                    + Create New Theme
                  </div>
                </>
              )}
              open={isDropdownVisible}
              onDropdownVisibleChange={setIsDropdownVisible}
            >
              {themeList &&
                themeList?.map((item) => (
                  <Option key={item.id} value={item.id}>
                    {item.name}
                  </Option>
                ))}
            </Select>
          </div>
        </div>
        {showThemeAttributes &&
          (loading ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                paddingBottom: 200,
                paddingTop: 200,
                width: '100%',
              }}
            >
              <Spin indicator={loadingSpin} />
            </div>
          ) : (
            <ThemeAttributes
              theme={theme}
              setTheme={setTheme}
              css={css}
              setCSS={setCSS}
              editMode={editMode}
              setShowThemeAttributes={setShowThemeAttributes}
            />
          ))}
      </div>
    </Card>
  );
}

export default SurveyThemeSetting;
export function ThemeAttributes({
  theme,
  setTheme,
  css,
  setCSS,
  editMode,
  setShowThemeAttributes,
}) {
  const [isBasicAttributesVisible, setIsBasicAttributesVisible] =
    useState(true);
  const font = useSelector(
    (state) => state.setting.productSetting.uploadedFonts
  );
  const createLoading = useSelector(
    (state) => state.channel.surveyTheme.createLoading
  );
  const dispatch = useDispatch();
  const isSaveEnabled = theme?.name?.trim();
  useEffect(() => {
    if (editMode) {
      setIsBasicAttributesVisible(true);
    }
  }, [editMode]);

  useEffect(() => {
    if (createLoading) {
      setTheme({});
      setCSS('');
      setShowThemeAttributes(false);
      false;
    }
  }, [createLoading]);

  function handleChangeProps(name, checked) {
    return {
      value: theme[name],
      onChange: (val) => {
        const newValue = checked
          ? val.target.checked
          : val?.target?.value ?? val;
        if (typeof val['persist'] === 'function') {
          val.persist();
        }

        setTheme((prev) => ({
          ...prev,
          [name]: newValue,
        }));
        if (name === 'enabledCSS') {
          setIsBasicAttributesVisible(false);
        }
      },
    };
  }
  const handleCancel = () => {
    setTheme({});
    setCSS('');
  };
  const handleSave = () => {
    dispatch(
      surveyThemeCreateFetch(
        theme?.enabledCSS ? { ...theme, advancedCss: css } : theme
      )
    );
  };
  return (
    <div className="theme-attributes-container">
      <div className="inner">
        <div className="theme-item">
          <label className="theme-label">Theme Name (for your use)</label>
          <Input
            className="item-label"
            placeholder="Theme Name"
            {...handleChangeProps('name')}
            style={{ minWidth: 216 }}
            disabled={editMode}
          />
        </div>
        <Checkbox
          disabled={editMode}
          style={{ marginBottom: 8 }}
          checked={isBasicAttributesVisible}
          onChange={(e) => {
            setIsBasicAttributesVisible(e.target.checked);
            setTheme((prev) => ({
              ...prev,
              enabledCSS: false,
            }));
          }}
        >
          Basic Theme Attributes
        </Checkbox>
        {isBasicAttributesVisible && (
          <div className="basic-attributes">
            <div className="theme-item">
              <label className="theme-label" style={{ marginTop: 8 }}>
                Border Color
              </label>
              <div className="color-picker-survey">
                <Input
                  disabled={editMode}
                  className="color-input"
                  type="color"
                  {...handleChangeProps('borderColor')}
                  style={{ width: '100%' }}
                />
                <Input
                  disabled={editMode}
                  className="text-input"
                  {...handleChangeProps('borderColor')}
                  maxLength={7}
                  style={{ marginTop: 4 }}
                />
              </div>
            </div>
            <div className="theme-item">
              <label className="theme-label">Background Color</label>
              <div className="color-picker-survey">
                <Input
                  disabled={editMode}
                  className="color-input"
                  type="color"
                  {...handleChangeProps('backgroundColor')}
                  style={{ width: '100%' }}
                />
                <Input
                  disabled={editMode}
                  className="text-input"
                  {...handleChangeProps('backgroundColor')}
                  maxLength={7}
                  style={{ marginTop: 4 }}
                />
              </div>
            </div>
            <div className="theme-item">
              <label className="theme-label">Button Color</label>
              <div className="color-picker-survey">
                <Input
                  {...handleChangeProps('buttonColor')}
                  className="color-input"
                  type="color"
                  style={{ width: '100%' }}
                  disabled={editMode}
                />
                <Input
                  {...handleChangeProps('buttonColor')}
                  className="text-input"
                  maxLength={7}
                  style={{ marginTop: 4 }}
                  disabled={editMode}
                />
              </div>
            </div>
            <div className="theme-item">
              <label className="theme-label">Button Font Color</label>
              <div className="color-picker-survey">
                <Input
                  {...handleChangeProps('buttonFontColor')}
                  className="color-input"
                  type="color"
                  style={{ width: '100%' }}
                  disabled={editMode}
                />
                <Input
                  {...handleChangeProps('buttonFontColor')}
                  className="text-input"
                  maxLength={7}
                  style={{ marginTop: 4 }}
                  disabled={editMode}
                />
              </div>
            </div>
            <div className="theme-item">
              <label className="theme-label">Font</label>
              <Select
                style={{ width: 216 }}
                showSearch
                optionFilterProp="children"
                {...handleChangeProps('font')}
                disabled={editMode}
              >
                {font?.map((font) => (
                  <Option key={font.id} value={font.id}>
                    {font.file.fileName}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="theme-item">
              <label className="theme-label">Font size</label>
              <Select
                style={{ width: 216 }}
                {...handleChangeProps('fontSize')}
                disabled={editMode}
              >
                {fontSizes.map((value) => (
                  <Option key={value} value={value}>
                    {value}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="theme-item">
              <label className="theme-label">Text Color</label>
              <div className="color-picker-survey">
                <Input
                  className="color-input"
                  type="color"
                  {...handleChangeProps('textColor')}
                  style={{ width: '100%' }}
                  disabled={editMode}
                />
                <Input
                  className="text-input"
                  maxLength={7}
                  {...handleChangeProps('textColor')}
                  style={{ marginTop: 4 }}
                  disabled={editMode}
                />
              </div>
            </div>
          </div>
        )}
        <Checkbox
          checked={theme?.enabledCSS || theme.advancedCss}
          {...handleChangeProps('enabledCSS', true)}
          style={{ whiteSpace: 'pre-wrap' }}
          disabled={editMode}
        >
          Advanced Styling
        </Checkbox>
        {(theme?.enabledCSS || theme.advancedCss) && (
          <ThemeCSSEditor value={css} onChange={setCSS} editMode={editMode} />
        )}
      </div>
      <div className="theme-buttons">
        <Button onClick={handleCancel} type="default" disabled={editMode}>
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={handleSave}
          disabled={editMode || !isSaveEnabled}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
